import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@chakra-ui/react';
import ParticipantUI from './participantUI';
import { useSelector } from 'react-redux';
import useResizeObserver from 'use-resize-observer';
import LimitAspectRatio from './limitAspectRatio';

const MIN_ASPECT_RATIO = 1.0;
const MAX_ASPECT_RATIO = 16 / 9;

const VideoSession = ({ screens, ...containerProps }) => {
  const [columnCount, setColumnCount] = useState(2);
  const [rowHeight, setRowHeight] = useState();
  const numberOfScreens = useSelector((state) => state.activeSession.screens.length);

  const onResize = useCallback(
    ({ width, height }) => {
      let bestColumnCount = 1;
      let bestFillRatio = 0;
      let columns = 0;
      while (columns < numberOfScreens) {
        columns++;
        const rows = Math.ceil(numberOfScreens / columns);
        const w = width / columns;
        const h = height / rows;
        const ar = w / h;
        let fillRatio = numberOfScreens / (columns * rows);
        if (ar < MIN_ASPECT_RATIO) {
          fillRatio *= ar / MIN_ASPECT_RATIO;
        } else if (ar > MAX_ASPECT_RATIO) {
          fillRatio *= MAX_ASPECT_RATIO / ar;
        }
        if (fillRatio > bestFillRatio) {
          bestFillRatio = fillRatio;
          bestColumnCount = columns;
        }
      }
      setColumnCount(bestColumnCount);

      const rowCount = Math.ceil(numberOfScreens / bestColumnCount);
      const rowHeight = Math.floor(height / rowCount);
      setRowHeight(rowHeight + 'px');
    },
    [numberOfScreens]
  );

  const ref = useRef();
  useResizeObserver({ ref, onResize });

  useEffect(() => {
    const grid = ref.current;
    if (grid) {
      const { clientWidth: width, clientHeight: height } = grid;
      onResize({ width, height });
    }
  }, [numberOfScreens, onResize]);

  return (
    <Grid
      ref={ref}
      height="100%"
      width="100%"
      templateColumns={`repeat(${columnCount}, 1fr)`}
      autoRows={rowHeight}
      gap={0}
      {...containerProps}
    >
      {screens.map((screen, index) => (
        <LimitAspectRatio
          key={screen.member ? screen.member.id : index}
          minRatio={MIN_ASPECT_RATIO}
          maxRatio={MAX_ASPECT_RATIO}
        >
          <ParticipantUI member={screen.member} useVideo={true} />
        </LimitAspectRatio>
      ))}
    </Grid>
  );
};

VideoSession.propTypes = {
  screens: PropTypes.array,
};

export default VideoSession;
